/**
 * ExpenseList.jsx — Variable expenses table
 *
 * Features:
 * - Columns: Date, Person, Category, Description, Amount, Job
 * - Search/filter by person or category
 * - "Add Expense" button
 * - Click row for inline edit or detail
 * - Delete button per row
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtC, fmtD, cn } from '../../lib/utils';
import { navigateToJob } from '../../lib/crossNav';
import { useExpenses, useDeleteExpense } from '../../hooks/useExpenses';
import { useJobs } from '../../hooks/useJobs';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../layout/Toast';

export default function ExpenseList({ onNewExpense, onSelectExpense, scope = 'all' }) {
  const navigate = useNavigate();
  const { data: allExpenses = [], isLoading, error } = useExpenses();
  const { data: jobs = [] } = useJobs();
  const { profile } = useAuth();
  const deleteMutation = useDeleteExpense();
  const { toast } = useToast();

  const selfName = profile?.full_name || '';
  // Pre-filter for personal scope: only this user's expenses
  const expenses = useMemo(() => {
    if (scope !== 'self') return allExpenses;
    if (!selfName) return [];
    return allExpenses.filter((e) => e.person === selfName);
  }, [allExpenses, scope, selfName]);

  // Job name helper
  const getJobTitle = (jobId) => {
    if (!jobId) return null;
    const job = jobs.find((j) => j.id === jobId);
    return job?.title || job?.client || null;
  };

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(expenses.map((e) => e.category).filter(Boolean))];
    return ['All', ...cats.sort()];
  }, [expenses]);

  // Filter expenses
  const filtered = useMemo(() => {
    let result = expenses;

    if (categoryFilter !== 'All') {
      result = result.filter((e) => e.category === categoryFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (e) =>
          (e.person || '').toLowerCase().includes(q) ||
          (e.description || '').toLowerCase().includes(q) ||
          (e.category || '').toLowerCase().includes(q)
      );
    }

    // Sort by date descending
    result.sort((a, b) => new Date(b.date) - new Date(a.date));

    return result;
  }, [expenses, search, categoryFilter]);

  const handleDelete = async (id) => {
    if (!confirm('Delete this expense?')) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast('Expense deleted');
    } catch (err) {
      toast('Failed to delete expense', 'error');
    }
  };

  if (error) {
    return (
      <div className="card p-4 text-red-accent text-sm">
        Failed to load expenses: {error.message}
      </div>
    );
  }

  // Calculate total
  const total = filtered.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading text-text-primary">{scope === 'self' ? 'My Expenses' : 'Variable Expenses'}</h2>
        <button
          onClick={onNewExpense}
          className="px-3 py-1.5 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors"
        >
          + Add Expense
        </button>
      </div>

      {/* Search */}
      <div>
        <input
          type="text"
          placeholder="Search by person, description, or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors"
        />
      </div>

      {/* Category filter */}
      <div className="flex gap-1 flex-wrap">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={cn(
              'px-2.5 py-1 text-2xs rounded-full transition-colors border',
              categoryFilter === cat
                ? 'bg-accent text-bg border-accent font-semibold'
                : 'bg-surface border-border text-text-dim hover:text-text-primary'
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Expense table */}
      <div className="flex-1 overflow-y-auto card p-0">
        {isLoading ? (
          <div className="p-4 text-center text-text-dim text-xs">Loading expenses...</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-text-dim text-xs">No expenses found</div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-2 border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Date</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Person</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Category</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Description</th>
                <th className="px-3 py-2 text-right font-semibold text-text-dim">Amount</th>
                <th className="px-3 py-2 text-left font-semibold text-text-dim">Job</th>
                <th className="px-3 py-2 text-center font-semibold text-text-dim">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((exp) => (
                <tr
                  key={exp.id}
                  onClick={() => onSelectExpense(exp)}
                  className="hover:bg-surface-2 transition-colors cursor-pointer"
                >
                  <td className="px-3 py-2 text-text-primary">{fmtD(exp.date)}</td>
                  <td className="px-3 py-2 text-text-primary font-semibold">{exp.person || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 rounded text-2xs bg-surface border border-border text-text-dim">
                      {exp.category || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-text-primary text-xs">{exp.description || '—'}</td>
                  <td className="px-3 py-2 text-right text-accent font-semibold">{fmtC(exp.amount)}</td>
                  <td className="px-3 py-2 text-xs">
                    {exp.jobId && getJobTitle(exp.jobId) ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigateToJob(navigate, exp.jobId); }}
                        className="text-accent hover:underline text-left"
                      >
                        {getJobTitle(exp.jobId)}
                      </button>
                    ) : (
                      <span className="text-text-dim">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(exp.id);
                      }}
                      disabled={deleteMutation.isPending}
                      className="px-2 py-0.5 text-2xs text-red-accent hover:bg-red-accent/10 rounded transition-colors disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Total footer */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between px-3 py-2 bg-surface border border-border rounded text-sm">
          <span className="text-text-dim font-semibold">Total:</span>
          <span className="text-accent font-heading">{fmtC(total)}</span>
        </div>
      )}
    </div>
  );
}
